import { useEffect, useState } from 'react';

import { SEO } from 'components/Seo/Seo';
import useAppLanguage from 'hooks/useAppLanguage';
import {
  dollarCurrencyCode,
  productExploreTabs,
  productExploreTabsKeys,
  productTypesForTabs,
} from 'utils/constants';
import NewsBanner from './components/NewsBanner/NewsBanner';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { TimeFrameKey } from 'data/time-frames';
import {
  ProductsWithHoldingsAndRates,
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import { z } from 'zod';
import ProductsTable from './components/ProductsTable';
import clsx from 'clsx';
import ProductsCategories from './components/ProductsCategories';
import ProductsSearch from './components/ProductsSearch';
import { ActiveProductDtoTypeEnum } from 'services/generated';
import ProductsPortfolio from './components/ProductsPortfolio';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import { formatCurrency } from 'utils/currencies';
import { useGetProfileQuery } from 'store/slices/profile-api';
import useBreakpoint from 'hooks/useBreakpoint';
import PortfolioCard from './components/PortfolioCard/PortfolioCard';
import { InvestmentJourneyModal } from './components/InvestmentJourneyModal';
import { getInvestmentJourneyStatus } from 'utils/investmentJourney';
import SelectionCategoryModal from './components/SelectionCategoryModal/SelectionCategoryModal';
import { newsBanners } from './components/NewsBanner/data';
import RequireInformationModal from 'app/_authenticated/tax-information/components/RequireInformationModal/RequireInformationModal';
import { useGetPersonalDataQuery } from 'store/slices/personalData-api';
import { useGetFeatureFlagsQuery } from 'store/slices/feature-flags-api';
import RebalanceInProgressModal from './components/RebalanceInProgressModal';
import { useAiInsights } from 'components/AiInsights/aiInsights-context';
import AiInsightsTabs from 'components/Tabs/AiInsightsTabs';
import { useProductsGraphs } from 'hooks/products/useGraphs';
import Joyride, { CallBackProps } from 'react-joyride';
import { tourSteps, TourStepTooltip, tourStyles } from './components/TourSteps';
import { getDisplayAssetCode } from '@altify/utils';

const toggleValuesKey = 'altify/hideValues';
const toggleTourKey = 'altify/hideTour';

const searchParamsSchema = z.object({
  type: fallback(z.enum(productExploreTabsKeys), 'all').default('all'),
});

export const Route = createFileRoute('/_authenticated/products/')({
  component: ProductExplore,
  validateSearch: zodValidator(searchParamsSchema),
});

function ProductExplore() {
  const tourClosedOrCompleted = localStorage.getItem(toggleTourKey);
  const { openAiInsights, question } = useAiInsights();
  const banners = newsBanners;
  useProductsWithHoldingsAndRates(dollarCurrencyCode);
  const navigate = useNavigate();
  const language = useAppLanguage();
  const { functionalCurrency: baseCurrency } = useFunctionalCurrency();

  const { data: personalData, isLoading: taxInformationLoading } =
    useGetPersonalDataQuery();
  const [showAdditionalInformationModal, setShowAdditionalInformationModal] =
    useState(false);
  const [showNewFeaturesTour, setShowNewFeaturesTour] = useState(false);

  const { gainsAndLosses, isLoading: isLoadingPortfolioGainsValue } =
    usePortfolioGainsAndLosses('24h');

  const { data: profileData, isLoading: isLoadingProfile } =
    useGetProfileQuery();

  const { data: featureFlags, isLoading: featureFlagsLoading } =
    useGetFeatureFlagsQuery();

  const rebalanceInProgess =
    featureFlags?.find(flag => flag.code === 'REBALANCE')?.active ?? false;
  const [showRebalanceInProgressModal, setShowRebalanceInProgressModal] =
    useState(rebalanceInProgess);
  const { isSmaller } = useBreakpoint();
  const isDeviceMobile = isSmaller('lg');

  const { type: tab } = Route.useSearch();
  const [displayCurrency, setDisplayCurrency] = useState<string>(
    baseCurrency?.code ?? dollarCurrencyCode,
  );
  const [timeFrameKey, setTimeFrameKey] = useState<TimeFrameKey>('24h');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectionCategoryIsOpen, setSelectionCategoryIsOpen] =
    useState<boolean>(false);

  const [hasDismissedNewsBanner, dismissNewsBanner] = useState(false);
  const [hidePortfolioValues, setHidePortfolioValues] =
    useState<boolean>(false);

  const [showInvestmentJourney, setShowInvestmentJourney] = useState<boolean>(
    (!profileData?.journeyCompleted && !isLoadingProfile) || false,
  );

  const [searchSelectedTab, setSearchSelectedTab] = useState<
    string | undefined
  >(undefined);
  const isSearching = searchQuery.length > 0;
  const activeTab = isSearching ? searchSelectedTab : tab;
  //for the portfolio data we should always show the values for the functional currency
  const { products: functionalProducts } = useProductsWithHoldingsAndRates(
    baseCurrency?.code ?? dollarCurrencyCode,
  );
  const { products, isLoading: isLoadingProducts } =
    useProductsWithHoldingsAndRates(displayCurrency);
  const productGraph = useProductsGraphs(timeFrameKey, {
    displayCurrency: displayCurrency,
  });

  const isLoading =
    isLoadingProducts || isLoadingPortfolioGainsValue || isLoadingProfile;

  const withdrawablAssets = products?.filter(
    _ => _.availableAmount > 0 && _.isWithdrawable,
  );
  const isVerified = profileData?.verificationStatus == 'Verified';
  const canDeposit = isVerified;
  const canWithdraw =
    (isVerified && withdrawablAssets && withdrawablAssets?.length > 0) || false;

  const filterProductsBySearchQuery = (
    products: ProductsWithHoldingsAndRates,
    query: string,
  ) => {
    return query === ''
      ? products
      : products
          .filter(
            product =>
              product.type !== ActiveProductDtoTypeEnum.Fiat &&
              product.isTracked,
          )
          .filter(
            product =>
              product.name.toLowerCase().includes(query.toLowerCase()) ||
              getDisplayAssetCode(product.code)
                ?.toLowerCase()
                .includes(query.toLowerCase()) ||
              productExploreTabs[language]
                .find(t => t.value === product.type.toLowerCase())
                ?.label?.toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              (product.code === 'PAXG' &&
                productExploreTabs[language]
                  .find(t => t.value === 'commodities')
                  ?.label?.toLowerCase()
                  .includes(searchQuery.toLowerCase())),
          );
  };

  const productTabs = productExploreTabs[language].filter(
    tab =>
      filterProductsBySearchQuery(
        getProductsForCategory(tab.value, products ?? [], isSearching),
        searchQuery,
      ).length > 0,
  );

  const activeTabIndex = productTabs.findIndex(
    item => item.value === activeTab,
  );

  const productsList = filterProductsBySearchQuery(
    getProductsForCategory(activeTab ?? 'all', products ?? [], isSearching),
    searchQuery,
  );

  const investmentJourneyStatus = profileData
    ? getInvestmentJourneyStatus(profileData)
    : null;

  const portfolioValue = functionalProducts?.reduce(
    (acc, curr) => acc + (curr.totalAmountInDisplay ?? 0),
    0,
  );

  const portfolioValueFormatted = formatCurrency(
    portfolioValue,
    baseCurrency?.code,
  );

  const reservedProducts = functionalProducts?.filter(
    p => p.reservedAmount > 0,
  );
  const reservedPortfolioValue = reservedProducts?.reduce(
    (acc, curr) => acc + (curr.reservedAmountInDisplay ?? 0),
    0,
  );

  const portfolioFiatValue = functionalProducts
    ?.filter(
      p =>
        p.type === ActiveProductDtoTypeEnum.Fiat ||
        p.type === ActiveProductDtoTypeEnum.Stablecoin,
    )
    .reduce((acc, curr) => acc + (curr.totalAmountInDisplay ?? 0), 0);

  const portfolioFiatValueFormatted = formatCurrency(
    portfolioFiatValue,
    baseCurrency?.code,
  );

  const portfolioReservedValueFormatted = formatCurrency(
    reservedPortfolioValue,
    baseCurrency?.code,
  );

  useEffect(() => {
    if (searchQuery === undefined || searchQuery === '') {
      setSearchSelectedTab(undefined);
    }
  }, [searchQuery]);

  const togglePortfolioValues = async (value: boolean) => {
    await localStorage.setItem(toggleValuesKey, value.toString());
    setHidePortfolioValues(value);
  };

  useEffect(() => {
    if (!taxInformationLoading && !personalData?.isComplete) {
      setShowAdditionalInformationModal(true);
    }
  }, [taxInformationLoading]);

  useEffect(() => {
    if (!profileData?.journeyCompleted) {
      setShowInvestmentJourney(true);
    }
  }, [isLoadingProfile]);

  useEffect(() => {
    if (!featureFlagsLoading) {
      setShowRebalanceInProgressModal(rebalanceInProgess);
    }
  }, [featureFlagsLoading]);

  // TODO: Refactor conditions into a single function and based on whether any steps potential viable for this page
  useEffect(() => {
    const canShowTour =
      !featureFlagsLoading &&
      !taxInformationLoading &&
      !showAdditionalInformationModal &&
      !showInvestmentJourney &&
      !showRebalanceInProgressModal &&
      !isLoading;
    if (canShowTour && tourClosedOrCompleted !== 'true') {
      setShowNewFeaturesTour(true);
    }
  }, [
    featureFlagsLoading,
    taxInformationLoading,
    showAdditionalInformationModal,
    showInvestmentJourney,
    showRebalanceInProgressModal,
    isLoading,
  ]);

  return (
    <>
      <SEO
        title="Products Explore"
        description="Product list, details, holdings, and savings."
        keywords="Product, Bitcoin, BTC, Ethereum, ETH, Savings, Vault, DeFi"
        page="productDetails"
      />
      {showNewFeaturesTour && (
        <>
          <Joyride
            steps={tourSteps}
            tooltipComponent={TourStepTooltip}
            styles={{ options: tourStyles }}
            callback={(data: CallBackProps) => {
              if (data.status === 'finished' || data.status === 'skipped') {
                localStorage.setItem(toggleTourKey, 'true');
              }
            }}
          />
        </>
      )}

      <div className="flex flex-col lg:flex-row gap-2 justify-between flex-wrap px-4">
        <div className="flex flex-col justify-between xl:flex-row w-full">
          <div
            className={clsx(
              'w-full lg:w-1/2 py-4 pb-1 hidden xl:flex flex-col flex-grow',
            )}
            id="portfolio-card-wrapper">
            <PortfolioCard
              timeFrame={timeFrameKey}
              togglePortfolioValues={togglePortfolioValues}
              hideValues={hidePortfolioValues}
              cashBalance={portfolioFiatValueFormatted}
              portfolioGainsAmount={gainsAndLosses?.gainAmount ?? 0}
              portfolioGainsPercentage={gainsAndLosses?.gainPercentage ?? 0}
              isLoading={isLoading}
              canWithdraw={canWithdraw}
              canDeposit={canDeposit}
              portfolioValue={portfolioValueFormatted}
              reservedPortfolioValue={portfolioReservedValueFormatted}
            />
          </div>
          <div
            className={clsx(
              'pr-2 lg:mt-8 mb-4 z-40 relative xl:hidden w-full',
              isSearching,
            )}
            id="explore-portfolio-wrapper">
            <ProductsPortfolio
              togglePortfolioValues={togglePortfolioValues}
              hideValues={hidePortfolioValues}
              isSearching={isSearching}
              portfolioValueFormatted={portfolioValueFormatted}
              portfolioGainsAmount={gainsAndLosses?.gainAmount ?? 0}
              portfolioGainsPercentage={gainsAndLosses?.gainPercentage ?? 0}
              isLoading={isLoadingPortfolioGainsValue}
              baseCurrencyCode={baseCurrency?.code}
              baseCurrencySymbol={baseCurrency?.symbol ?? undefined}
              currentRewardTierName={profileData?.currentRewardTierName ?? ''}
            />
          </div>
          <div
            className={clsx(
              'py-4 pb-1 mb-2 xl:flex flex-col flex-grow w-full xl:w-1/2',
              banners?.filter(_ => _.active === true)?.length > 1
                ? 'mb-8 lg:mb-4'
                : 'lg:mb-0',
              hasDismissedNewsBanner ? 'hidden md:visible' : '',
            )}
            id="products-banner-wrapper">
            <NewsBanner
              banners={banners}
              onClose={() => {
                dismissNewsBanner(true);
              }}
              className="pt-3 lg:pt-0"
            />
          </div>
        </div>
        <div className="flex flex-col w-full min-w-0">
          <div
            className={clsx(
              'sticky top-0 bg-body-background w-full flex pb-8 overflow-x-auto no-scrollbar z-10 insight-step',
              !isDeviceMobile && 'whitespace-nowrap p-2 px-0',
              isDeviceMobile && '',
            )}>
            <AiInsightsTabs
              pageName="Explore"
              onClickItem={(question: string) => {
                openAiInsights(question, 'Explore');
              }}
              className="pl-2 gap-2"
            />
          </div>
          <div className="md:border-2 mb:border-top-0 md:border-dark-blue md:rounded-xxl mb-12 lg:mb-24">
            <div className="flex flex-col gap-4 sticky top-[3rem] top-8 md:top-16 z-30 bg-body-background md:border-2 md:border-b-0 md:border-dark-blue md:rounded-xxl md:rounded-b-none -mt-5 md:px-4 md:py-4 -ml-[1px] -mr-[2px]">
              <div className="flex flex-col gap-4 px-2 md:px-1 py-4">
                <ProductsSearch
                  productTabsLength={productTabs.length}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  displayCurrency={displayCurrency}
                  setDisplayCurrency={setDisplayCurrency}
                  timeframeKey={timeFrameKey}
                  setTimeframeKey={setTimeFrameKey}
                  baseCurrencyCode={baseCurrency?.code}
                  baseCurrencySymbol={baseCurrency?.symbol ?? undefined}
                  routes={productTabs}
                  activeIndex={activeTabIndex}
                  setSelectionCategoryIsOpen={setSelectionCategoryIsOpen}
                />
                <ProductsCategories
                  productTabs={productTabs}
                  onClickItem={tab => {
                    if (isSearching) {
                      setSearchSelectedTab(tab.value);
                    } else {
                      navigate({
                        to: '/products',
                        search: { type: tab.value },
                      });
                    }
                  }}
                  activeIndex={activeTabIndex}
                  isMobile={isDeviceMobile}
                />
              </div>
            </div>
            <div className="md:px-4 md:pb-4">
              <ProductsTable
                isMobile={isDeviceMobile}
                products={productsList ?? []}
                displayCurrency={displayCurrency}
                timeFrameKey={timeFrameKey}
                isLoading={isLoadingProducts}
                isSearching={isSearching}
              />
            </div>
          </div>
        </div>

        <InvestmentJourneyModal
          isModalOpen={showInvestmentJourney}
          investmentJourneyStatus={investmentJourneyStatus}
          onClose={() => {
            setShowInvestmentJourney(false);
          }}
        />
        <SelectionCategoryModal
          isOpen={selectionCategoryIsOpen}
          onClose={() => setSelectionCategoryIsOpen(false)}
          categories={productTabs}
          onSelect={(category: any) => {
            navigate({
              to: '/products',
              search: { type: category },
            });
          }}
        />
        
        <RebalanceInProgressModal
          isOpen={
            showRebalanceInProgressModal &&
            !showAdditionalInformationModal &&
            !selectionCategoryIsOpen &&
            !showInvestmentJourney
          }
          handleClose={() => setShowRebalanceInProgressModal(false)}
        />
      </div>
    </>
  );
}

function getProductsForCategory(
  category: string,
  products: ProductsWithHoldingsAndRates,
  isSearch: boolean,
) {
  const validProductTypes = [...productTypesForTabs[category?.toLowerCase()]];
  switch (category?.toLowerCase()) {
    case 'crypto':
      return products?.filter(
        p => p.code !== 'PAXG' && p.type === ActiveProductDtoTypeEnum.Crypto,
      );

    case 'commodities':
      return products?.filter(
        p =>
          p.code === 'PAXG' ||
          validProductTypes.includes(p.type?.toLowerCase()),
      );
    default:
      return products?.filter(
        p =>
          validProductTypes.includes(p.type?.toLowerCase()) &&
          (p.isTracked || category?.toLowerCase() !== 'all'),
      );
  }
}

export default ProductExplore;
